import React from 'react';
import PropTypes from 'prop-types';

/**
 * Shared shell for the new talent dashboard cards.
 * Replaces the class-based DashboardPanel (header, count badge, last-updated pill,
 * disabled / inactive states) with a Tailwind functional component.
 */

export const CountBadge = ({ count }) => {
    const value = parseInt(count) || 0;
    if (!value) return null;

    return (
        <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-danger px-1.5 text-xs font-semibold leading-none text-white">
            {value}
        </span>
    );
};

// prefix="Updated" → "Updated 3 days ago" / "Updated today"; prefix="" → "3 days ago" / "Today"
export const LastUpdatedPill = ({ days, staleAfter = 30, prefix = 'Updated' }) => {
    if (!TB.isInteger(days)) return null;

    const isStale = days > staleAfter;
    const ago = days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`;
    const label = prefix ? `${prefix} ${ago}` : TB.upperFirst(ago);

    return (
        <span
            className={TB.mapClass('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold', {
                'bg-amber-50 text-amber-700': isStale,
                'bg-gray-100 text-gray-500': !isStale,
            })}
        >
            {isStale ? <span aria-hidden="true">!</span> : <i className="fas fa-clock text-[10px]" aria-hidden="true" />}
            {label}
        </span>
    );
};

const StatePill = ({ children }) => (
    <span className="inline-flex items-center whitespace-nowrap rounded-full bg-primary-lightest/20 px-3 py-1 text-xs font-semibold text-primary">
        {children}
    </span>
);

const DashboardCard = ({
    title,
    count,
    headerRight,
    footer,
    children,
    className = '',
    bodyClassName = '',
    disabled = false,
    disabledReason = 'Your profile must be complete and your submission accepted in order to accept projects.',
    disabledBadgeMessage = 'Not Available Yet!',
    inactive = false,
    inactiveMessage = 'not available yet',
    inactiveBadgeMessage = 'Coming Soon!',
}) => {
    const isBlocked = disabled || inactive;

    return (
        <section
            className={TB.mapClass(
                `flex h-full min-w-0 flex-col rounded-2xl bg-white p-5 shadow-[0_1px_3px_rgba(16,24,40,0.06),0_1px_2px_rgba(16,24,40,0.04)] sm:p-5 ${className}`,
                { 'opacity-80': isBlocked }
            )}
        >
            <header className="mb-5 flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                    <h2 className="m-0 truncate text-lg font-semibold leading-tight text-gray-900 sm:text2xl">
                        {title}
                    </h2>
                    {!isBlocked && <CountBadge count={count} />}
                </div>

                {inactive ? (
                    <StatePill>{inactiveBadgeMessage}</StatePill>
                ) : disabled ? (
                    <StatePill>{disabledBadgeMessage}</StatePill>
                ) : (
                    headerRight
                )}
            </header>

            {isBlocked ? (
                <p className="m-0 text-sm text-gray-500">
                    {inactive ? inactiveMessage : disabledReason}
                </p>
            ) : (
                <>
                    <div className={`flex-1 ${bodyClassName}`}>
                        {children}
                    </div>

                    {footer && (
                        <footer className="mt-4 flex flex-wrap items-center justify-between gap-3">
                            {footer}
                        </footer>
                    )}
                </>
            )}
        </section>
    );
};

DashboardCard.propTypes = {
    title: PropTypes.node,
    count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    headerRight: PropTypes.node,
    footer: PropTypes.node,
    className: PropTypes.string,
    bodyClassName: PropTypes.string,
    disabled: PropTypes.any,
    disabledReason: PropTypes.node,
    disabledBadgeMessage: PropTypes.node,
    inactive: PropTypes.bool,
    inactiveMessage: PropTypes.node,
    inactiveBadgeMessage: PropTypes.node,
};

export default DashboardCard;
